import { motion } from "framer-motion";
import React, { useEffect } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import Overlay from "../../../../../checkEligibilty/components/modals/Overlay";
import { ContainerVariants } from "../../../../../checkEligibilty/components/modals/variant";
import { mediaObj } from "../../../../../../../styles/Media";
import { CheckIcon } from "lucide-react";
import { BillingType, fibiBillingData } from "./data";
import { useSnapshot } from "valtio";
import StarterBillingCard from "./StarterBillingCard";
import { Zap, Bell, CreditCard, XCircle } from "lucide-react";
import { useGetBusinessProfile } from "../../../../../../../hooks/business/useAuth";
import { useSubscriptionPayment } from "../../../../../../../hooks/business/useBilling";
import { isBusinessDataStore } from "../../../../../../../store/isBusinessData";
import { SpinnerWrapper } from "../../../../../components/styles";
import Spinner from "../../../../../../../utils/Spinner";
import { loginFailed } from "../../../../../../../utils";

const trialInfo = [
  {
    label: "Right now",
    value: "All Pro features are available instantly.",
    Icon: Zap,
  },
  {
    label: "We’ll remind you",
    value: "You’ll get a reminder 3 days before your trial ends—no surprises.",
    Icon: Bell,
  },
  {
    label: "Only pay if you continue",
    value:
      "Your subscription starts automatically unless you cancel before the trial ends.",
    Icon: CreditCard,
  },
  {
    label: "Cancel anytime with one click",
    value: "No forms, no hassle—just one tap to cancel.",
    Icon: XCircle,
  },
];

const StartingModal = () => {
  const { isBusinessData } = useSnapshot(isBusinessDataStore);
  const { data: businessUserData, isLoading: businessDataLoading } =
    useGetBusinessProfile(isBusinessData);
  const { mutate: submutate, isLoading: subLoading } = useSubscriptionPayment();

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const onSubmit = (params: BillingType, paystackPlan: string) => {
    const payload = {
      billingType: params,
      isNigeria: false,
    };

    submutate(payload, {
      onSuccess: (data) => {
        window.location.replace(data.data.url);
      },
    });
  };

  const onClick = (params: BillingType, paystackPlan: string) => {
    onSubmit(params, paystackPlan);
  };

  const masterLoading = subLoading || businessDataLoading;

  return createPortal(
    <Overlay onClose={() => {}}>
      <Wrapper
        onClick={(e) => e.stopPropagation()}
        variants={ContainerVariants}
      >
        <div className="h-max lg:h-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 lg:p-6 rounded-xl gap-4 h-full lg:h-[92%]">
            <div className="flex flex-col gap-6 lg:overflow-y-auto">
              <h5 className=" text-2xl font-black">
                Start your free 30 days Pro trial
              </h5>

              <div className="flex flex-col gap-4">
                {[
                  "Interview Simulations",
                  "Real-Time Feedback & Personalized Recommendations",
                  "Access to Curated Question Library",
                  "Transcription in Document",
                  "Practice History Storage",
                ].map((item, index) => (
                  <p
                    key={index}
                    className="text-sm text-[#1B092C] flex items-center gap-2"
                  >
                    <CheckIcon className=" text-primaryColor" />
                    {item}
                  </p>
                ))}
              </div>

              <div className=" flex items-center gap-2">
                <div className="w-full h-[1px] bg-[#0076E9]" />
                <div className="text-sm whitespace-nowrap">
                  Choose your plan
                </div>
                <div className="w-full h-[1px] bg-[#0076E9]" />
              </div>

              {masterLoading ? (
                <SpinnerWrapper>
                  <Spinner size={40} />
                </SpinnerWrapper>
              ) : (
                <div className="flex flex-col gap-4">
                  {fibiBillingData().map((ev) => (
                    <StarterBillingCard
                      data={ev}
                      onButtonClick={onClick}
                      currentPlan={
                        businessUserData?.paymentInfo?.subscriptionType
                      }
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-xl p-6 bg-[#0076E914] hidden lg:flex flex-col gap-6 sticky top-0 right-0">
              <h5 className="text-2xl font-semibold">
                What happens after you
                <span className="font-bold text-primaryColor">
                  {" "}
                  start your trial?
                </span>
              </h5>

              <div className="flex items-start gap-4 h-full">
                <div className="flex flex-col justify-between h-full bg-primaryColor rounded-full p-6">
                  {trialInfo.map((item, index) => (
                    <div key={index}>
                      {<item.Icon className=" text-white w-5 h-5" />}
                    </div>
                  ))}
                </div>
                <div className="flex flex-col justify-between h-full mt-2">
                  {trialInfo.map((item, index) => {
                    const isLast = index === trialInfo.length - 1;
                    return (
                      <div key={index} className={`flex flex-col gap-2 text-xl ${isLast ? "mb-4" : ""}`}>
                        <h6 className="text font-semibold text-[#1B092C]">
                          {item.label}
                        </h6>
                        <p className="text-sm text-[#1B092C]">{item.value}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="flex bg-white items-center flex-col gap-2 pb-4 text-center">
            Your trial is completely free — explore all features before making a
            decision.
            <button
              className=" text-primaryColor underline"
              onClick={() => {
                loginFailed();
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </Wrapper>
    </Overlay>,
    document.body
  );
};

export default StartingModal;

const Wrapper = styled(motion.div)`
  width: 80vw;
  height: 90vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 12px;
  padding: 40px 32px;

  ${mediaObj.mediumDesktop} {
    margin: auto;
    width: 90vw;
    padding-left: 5%;
    padding-right: 5%;
  }

  ${mediaObj.smallDesktop} {
    margin: auto;
    width: 65vw;
    padding-left: 5%;
    padding-right: 5%;
  }

  ${mediaObj.tablet} {
    width: 80vw;
  }

  ${mediaObj.bigMobile} {
    width: 90vw;
  }
`;
